import {
  Button,
  Table,
  Tag,
  Tooltip,
  useNotifications,
} from '@ph-react-ui/core';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../hooks/auth/useAuth';
import { useDownloadFile } from '../../../../../hooks/files/useDownloadFile';
import { useGeneratePdfReceipt } from '../../../../../hooks/payments/useGeneratePdfReceipt';
import { usePaginatedHistoryOfSchoolTaxPayments } from '../../../../../hooks/taxes/school-taxes/parent/history/usePaginatedHistoryOfSchoolTaxPayments';
import { displayPrice } from '../../../../../utils/displayPrice';
import { PaymentType } from '../../../../../utils/enums/payment-type.enum';
import { ScopeAccess } from '../../../../../utils/enums/scope-access.enum';
import { EmptyState } from '../../../../shared/components/EmptyState';
import { PageTitle } from '../../../../shared/components/PageTitle';
import { Pagination } from '../../../../shared/components/Pagination';
import { TableSkeleton } from '../../../../shared/components/skeletons/TableSkeleton';
import {
  getRowClassName,
  isPenaltyApplied,
  renderPenaltyCell,
} from '../utils/schoolTaxesUtils';

export function SchoolTaxesHistory() {
  const { authState } = useAuth();
  const canMakePayments = authState?.scope?.includes(ScopeAccess.MakePayments);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notification = useNotifications();

  const [searchParams] = useSearchParams();
  const { data, isSuccess, isPending } =
    usePaginatedHistoryOfSchoolTaxPayments(searchParams);

  const headers = useMemo(() => {
    const baseHeaders = [
      t('TAXES.TABLE.HEADERS.STUDENT'),
      t('TAXES.TABLE.HEADERS.AMOUNT'),
      t('TAXES.TABLE.HEADERS.STATUS'),
      t('TAXES.TABLE.HEADERS.SCHOOL_YEAR'),
      t('TAXES.TABLE.HEADERS.PERCENT'),
      t('PAYMENTS.TABLE.LABELS.DUE_DATE'),
      t('PAYMENTS.TABLE.LABELS.PENALTY'),
      t('TAXES.TABLE.HEADERS.PAYMENT_DATE'),
      t('TAXES.TABLE.HEADERS.PAID_WITH'),
      t('TAXES.TABLE.HEADERS.NOTES'),
    ];
    if (canMakePayments) {
      baseHeaders.push(t('TAXES.TABLE.HEADERS.ACTIONS'));
    }
    return baseHeaders;
  }, [t, canMakePayments]);

  const onPayNow = useCallback(
    (id: string, studentId: string, year?: number) => {
      const params = new URLSearchParams({
        withFallback: 'true',
        ...(year && { year: String(year) }),
      });

      navigate(
        `/dashboard/payments/school-taxes/pre-payment/${id}/${studentId}?${params.toString()}`
      );
    },
    [navigate]
  );

  const downloadFile = useDownloadFile();
  const generateReceipt = useGeneratePdfReceipt();

  const onDownloadReceipt = useCallback(
    (id: string, fileId: string | null) => {
      if (fileId) {
        downloadFile
          .mutateAsync(fileId)
          .then((res) => window.open(res.uri))
          .catch((resError) => {
            notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
          });
      } else {
        generateReceipt
          .mutateAsync({ id, type: PaymentType.SchoolFee })
          .then((receiptId) => {
            if (receiptId) {
              downloadFile
                .mutateAsync(receiptId as string)
                .then((res) => window.open(res.uri))
                .catch((resError) => {
                  notification.danger(
                    t(`NETWORK_ERRORS.${resError.errors[0]}`)
                  );
                });
            }
          })
          .catch((resError) => {
            notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
          });
      }
    },
    [downloadFile, generateReceipt, notification, t]
  );

  const tableChildren = useMemo(() => {
    if (!data?.result) return [];

    // Sort by student (name), then year (startYear), then installment index
    const sortedData = [...data.result].sort((a, b) => {
      if (a.name !== b.name) return a.name.localeCompare(b.name);
      if (a.startYear !== b.startYear) return a.startYear - b.startYear;
      return a.index - b.index;
    });

    const firstUnpaidIndexMap = new Map<string, number>();

    sortedData.forEach((user) => {
      const key = `${user.userId}-${user.startYear}`;
      const existing = firstUnpaidIndexMap.get(key);

      if (!user.isPaid) {
        if (existing === undefined || user.index < existing) {
          firstUnpaidIndexMap.set(key, user.index);
        }
      }
    });

    return sortedData.reduce((acc: any, user) => {
      const penaltyApplied = isPenaltyApplied(user);
      const key = `${user.userId}-${user.startYear}`;
      const firstUnpaidIndex = firstUnpaidIndexMap.get(key);
      const isFirstUnpaid = !user.isPaid && user.index === firstUnpaidIndex;
      const canPayThisInstallment = !user.isPaid && isFirstUnpaid;
      const rowClassName = getRowClassName(user.isPaid, penaltyApplied);

      let tooltipMessage = '';
      let showTooltip = false;

      if (user.isPaid) {
        tooltipMessage = t('TAXES.MESSAGES.INSTALLMENT_ALREADY_PAID');
        showTooltip = true;
      } else if (!canPayThisInstallment) {
        tooltipMessage = t('PAYMENTS.TABLE.LABELS.PAY_PREVIOUS_FIRST');
        showTooltip = true;
      }

      let userArray = [
        <span className={rowClassName}>{user.name}</span>,
        <span className={rowClassName}>
          {displayPrice(
            user.amount,
            t('TAXES.LABELS.BGN'),
            t('TAXES.LABELS.EUR'),
            user.currency
          )}
        </span>,
        user.isPaid ? (
          <Tag type="success">{t('TAXES.STATUS.PAID')}</Tag>
        ) : (
          <Tag type="accent">{t('TAXES.STATUS.PENDING')}</Tag>
        ),
        <span className={rowClassName}>
          {user.startYear} - {user.endYear}
        </span>,
        <span className={rowClassName}>{user.percentage}%</span>,
        <span className={rowClassName}>
          {user.dueDate ? format(new Date(user.dueDate), 'dd.MM.yyyy') : '-'}
        </span>,
        renderPenaltyCell({
          installment: {
            penalty: user.penalty,
            dueDate: user.dueDate,
            gracePeriod: user.gracePeriod,
            isPaid: user.isPaid,
            currency: user.currency,
          },
          t,
        }),
        <span className={rowClassName}>
          {user.isPaid && user.paidAt
            ? format(new Date(user.paidAt), 'dd.MM.yyyy')
            : ''}
        </span>,
        <span className={rowClassName}>
          {user.isPaid
            ? user.isPaidWithCard
              ? t('TAXES.LABELS.CARD')
              : t('TAXES.LABELS.TRANSFER')
            : ''}
        </span>,
        user.note ? (
          <span className="taxes__school__table__row__info">
            <Tooltip
              className="taxes__school__table__row__tooltip"
              message={user.note}
            >
              {user.note.slice(0, 25) + '...'}
            </Tooltip>
          </span>
        ) : (
          ''
        ),
      ];

      if (canMakePayments) {
        if (!user.isPaid) {
          const payButton = (
            <Button
              variant="outlined"
              disabled={!canPayThisInstallment}
              onClick={() => onPayNow(user.id, user.userId, user.startYear)}
            >
              {t('ACTIONS.PAY_NOW')}
            </Button>
          );

          userArray.push(
            showTooltip ? (
              <Tooltip position="left" message={tooltipMessage}>
                {payButton}
              </Tooltip>
            ) : (
              payButton
            )
          );
        } else if (user.isPaidWithCard) {
          userArray.push(
            <Button
              variant="outlined"
              onClick={() => onDownloadReceipt(user.id, user.fileMetadataId)}
            >
              {t('ACTIONS.DOWNLOAD_RECEIPT')}
            </Button>
          );
        } else {
          userArray.push('');
        }
      }

      return [...acc, userArray];
    }, []);
  }, [data?.result, t, onPayNow, onDownloadReceipt, canMakePayments]);

  return (
    <div className="taxes__school">
      <PageTitle
        title={t('TAXES.TITLES.SCHOOL_TAXES_HISTORY')}
        subtitle={t('TAXES.TITLES.SCHOOL_TAXES')}
      />
      {isPending && <TableSkeleton />}

      {isSuccess &&
        data &&
        (data.totalCount > 0 ? (
          <>
            <Table headers={headers} rows={tableChildren} />
            <div className="users__pagination">
              <Pagination data={data} />
            </div>
          </>
        ) : (
          <EmptyState />
        ))}
    </div>
  );
}
